/**
 * ChatPulseCompose — /thread/new
 *
 * Rich-editor composer for discussions + questions on ChatsPulse.
 * Bundles an AI Assistant panel with starter prompts, title suggestions,
 * "turn into a question", "improve/expand draft", and tag suggestions —
 * so writers who don't know what to post yet still get moving.
 *
 * Query-string knobs:
 *   ?kind=question               → pre-select "Ask a Question"
 *   ?scope=near                  → hint that this is "Post About My Area"
 *   ?category=food&city=Portland → prefill filters
 */
import { useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, HelpCircle, MapPin, Tag, ArrowLeft, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import RichEditor from "@/components/RichEditor";
import AIAssistant from "@/components/AIAssistant";
import useTransliterate from "@/hooks/useTransliterate";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/chatpulse/v1`;

const CATEGORY_OPTIONS = [
  "local-news", "community", "travel", "food", "farming", "business",
  "education", "health", "nature", "science", "technology", "sports",
  "movies", "music", "real-estate", "jobs", "events", "family-alumni",
  "transport",
];

const LANG_OPTIONS = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "hi", label: "Hindi" },
  { code: "ml", label: "Malayalam" },
  { code: "ta", label: "Tamil" },
  { code: "te", label: "Telugu" },
  { code: "kn", label: "Kannada" },
  { code: "bn", label: "Bengali" },
  { code: "mr", label: "Marathi" },
  { code: "gu", label: "Gujarati" },
  { code: "pa", label: "Punjabi" },
  { code: "ur", label: "Urdu" },
  { code: "ar", label: "Arabic" },
  { code: "zh", label: "Chinese" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
];

export default function ChatPulseCompose() {
  const [params]  = useSearchParams();
  const nav       = useNavigate();
  const { user }  = useAuth();

  const [title, setTitle]     = useState("");
  const [body, setBody]       = useState("");
  const [country, setCountry] = useState(params.get("country") || "");
  const [state, setState]     = useState(params.get("state")   || "");
  const [city, setCity]       = useState(params.get("city")    || "");
  const [category, setCategory] = useState(params.get("category") || "community");
  const [tags, setTags]       = useState("");
  const [language, setLanguage] = useState("en");
  const [busy, setBusy]       = useState(false);

  const titleRef = useRef(null);
  const titleTranslit = useTransliterate({
    value: title, onChange: setTitle, inputRef: titleRef, testIdPrefix: "compose-title-translit",
  });

  const isQuestion = params.get("kind") === "question";
  const kind = isQuestion ? "question" : "discussion";
  const Icon = isQuestion ? HelpCircle : MessageCircle;

  const canSubmit = useMemo(() => title.trim().length >= 3 && !busy, [title, busy]);

  const applyTagFromAI = (t) => {
    const clean = (t || "").trim();
    if (!clean) return;
    const list = tags.split(",").map((x) => x.trim()).filter(Boolean);
    if (list.includes(clean)) return;
    setTags([...list, clean].join(", "));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!user) {
      toast.error("Please sign in with SocialPulse before starting a thread.");
      nav("/auth/socialpulse-login");
      return;
    }
    setBusy(true);
    try {
      const cleanTags = tags.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 10);
      const loc = { country: country.trim(), state: state.trim(), city: city.trim() };
      Object.keys(loc).forEach((k) => !loc[k] && delete loc[k]);

      const { data } = await axios.post(
        `${API}/threads`,
        {
          context_type: "site",
          context_id:   "chatspulse",
          title:        title.trim(),
          body_html:    body.trim() || null,
          category,
          tags:         cleanTags.length ? cleanTags : null,
          location:     Object.keys(loc).length ? loc : null,
          kind,
          language,
        },
        { withCredentials: true },
      );
      toast.success(isQuestion ? "Question posted!" : "Discussion started!");
      nav(`/thread/${data.thread?.id || data.id || ""}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Something went wrong";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-6 py-8" data-testid="chatpulse-compose">
      <Link to="/" className="text-xs uppercase tracking-widest text-slate-400 hover:text-blue-700 font-semibold inline-flex items-center gap-1" data-testid="compose-back">
        <ArrowLeft className="w-3 h-3" /> Back
      </Link>

      <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
        <Icon className="w-3.5 h-3.5" /> {isQuestion ? "Ask a Question" : "Start a Discussion"}
      </div>

      <h1 className="mt-4 text-2xl sm:text-3xl font-black tracking-tight text-slate-900">
        {isQuestion ? "What would you like to know?" : "Start a conversation"}
      </h1>
      <p className="text-sm text-slate-600 mt-1">Add a place and category so the right community can find your thread.</p>

      {/* AI Assistant — encourage people who don't know what to post yet */}
      <div className="mt-5">
        <AIAssistant
          topic={title}
          category={category}
          location={[city, state, country].filter(Boolean).join(", ")}
          currentTitle={title}
          currentBody={body}
          surface={isQuestion ? "question" : "thread"}
          language={language}
          onApplyTitle={(t) => setTitle(t)}
          onApplyBody={(b) => setBody(b)}
          onApplyTags={applyTagFromAI}
          testIdPrefix="compose-ai"
        />
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <label className="block">
          <span className="block text-xs font-bold text-slate-700 mb-1">Title <span className="text-red-500">*</span></span>
          <div className="relative">
            <input
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={titleTranslit.onKeyDown}
              required minLength={3} maxLength={200}
              placeholder={isQuestion ? "e.g. What vaccines are required for travel to Japan?" : "e.g. Weekend markets in London worth visiting"}
              className="w-full px-4 py-2.5 pr-20 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400 bg-white"
              data-testid="compose-title"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">{titleTranslit.chip}</div>
          </div>
        </label>

        <div>
          <label className="block text-xs font-bold text-slate-700 mb-1">Body (optional)</label>
          <RichEditor
            value={body}
            onChange={setBody}
            placeholder={isQuestion
              ? "Add context to help people give you the best answer — details, links, what you've tried…"
              : "Add extra context, links, or specifics that help people join in."}
            testIdPrefix="compose-body-editor"
          />
        </div>

        <fieldset className="rounded-xl border border-slate-200 p-4">
          <legend className="px-2 text-xs font-bold text-slate-700 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3 text-blue-600" /> Location (optional)
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1">
            <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country" className="px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400" data-testid="compose-country" />
            <input value={state}   onChange={(e) => setState(e.target.value)}   placeholder="State / Province" className="px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400" data-testid="compose-state" />
            <input value={city}    onChange={(e) => setCity(e.target.value)}    placeholder="City / Town" className="px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400" data-testid="compose-city" />
          </div>
        </fieldset>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="block text-xs font-bold text-slate-700 mb-1">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400 bg-white"
              data-testid="compose-category"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>{c.replace("-", " ").replace(/\b\w/g, (m) => m.toUpperCase())}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-bold text-slate-700 mb-1">Language</span>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400 bg-white"
              data-testid="compose-language"
            >
              {LANG_OPTIONS.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-bold text-slate-700 mb-1">Tags (comma-separated)</span>
            <div className="relative">
              <Tag className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="Travel, Japan, Cherry Blossoms"
                className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-400"
                data-testid="compose-tags"
              />
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-slate-500">
            {user
              ? `Posting as ${user.name || user.email || user.id}`
              : "You'll be asked to sign in with SocialPulse."}
          </p>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="compose-submit"
          >
            <Send className="w-3.5 h-3.5" />
            {busy ? "Posting…" : isQuestion ? "Post Question" : "Start Discussion"}
          </button>
        </div>
      </form>
    </div>
  );
}
